import { useState } from 'react';
import { Drawer, IconButton } from '@mui/material';
import {
  MdClose,
  MdDarkMode,
  MdLightMode,
  MdLogout,
} from 'react-icons/md';
import { Logo } from '@components/ui/Logo';
import { useAuth, useLogout } from '@features/auth/hooks';
import { useColorMode } from '@hooks/useColorMode';
import { SidebarNav } from './SidebarNav';
import { SidebarTopFade } from './SidebarTopFade';
import { SignOutConfirmDialog } from './SignOutConfirmDialog';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function MobileSidebarDrawer({ open, onClose }: Props) {
  const { user, roles } = useAuth();
  const logout = useLogout();
  const { mode, toggle } = useColorMode();
  const isDark = mode === 'dark';
  const themeLabel = isDark ? 'Switch to light mode' : 'Switch to dark mode';
  const [confirmSignOut, setConfirmSignOut] = useState(false);

  const initial = (user?.email?.[0] ?? 'U').toUpperCase();

  const handleConfirmSignOut = () => {
    setConfirmSignOut(false);
    logout.mutate();
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      anchor="left"
      PaperProps={{
        sx: {
          width: 280,
          maxWidth: '85vw',
          backgroundColor: 'rgb(var(--color-surface))',
          backgroundImage: 'none',
          borderRight: '1px solid rgb(var(--color-border))',
        },
      }}
    >
      <div className="relative flex h-full flex-col">
        <SidebarTopFade />

        <div className="relative flex items-center gap-3 px-3 py-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white shadow-soft ring-1 ring-border">
            <Logo variant="mark" height={22} />
          </div>
          <div className="min-w-0 flex-1 leading-tight">
            <div className="truncate text-[14px] font-semibold tracking-tight text-foreground">
              Trendz
            </div>
            <div className="text-[10px] font-medium uppercase tracking-[0.2em] text-foreground-muted">
              Master Data
            </div>
          </div>
          <IconButton size="small" onClick={onClose} aria-label="Close menu">
            <MdClose />
          </IconButton>
        </div>

        <SidebarNav roles={roles} collapsed={false} onItemActivate={onClose} />

        <div className="relative border-t border-border px-3 py-3">
          <div className="mb-2 flex items-center gap-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[12px] font-semibold uppercase text-primary">
              {initial}
            </div>
            <div className="min-w-0 flex-1 text-[12px] leading-tight">
              <div className="truncate text-foreground">
                {user?.email ?? 'Signed in'}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={toggle}
            aria-label={themeLabel}
            aria-pressed={isDark}
            className="flex h-10 w-full items-center gap-2 rounded-md px-3 text-[13px] font-medium text-foreground-muted transition-colors hover:bg-primary/[0.08] hover:text-foreground"
          >
            {isDark ? <MdLightMode size={18} /> : <MdDarkMode size={18} />}
            <span>{isDark ? 'Light mode' : 'Dark mode'}</span>
          </button>
          <button
            type="button"
            onClick={() => setConfirmSignOut(true)}
            disabled={logout.isPending}
            className="mt-1 flex h-10 w-full items-center gap-2 rounded-md px-3 text-[13px] font-medium text-foreground-muted transition-colors hover:bg-danger/10 hover:text-danger disabled:cursor-not-allowed disabled:opacity-60"
          >
            <MdLogout size={18} />
            <span>{logout.isPending ? 'Signing out…' : 'Sign out'}</span>
          </button>
        </div>
      </div>

      <SignOutConfirmDialog
        open={confirmSignOut}
        onClose={() => setConfirmSignOut(false)}
        onConfirm={handleConfirmSignOut}
        isPending={logout.isPending}
        idSuffix="mobile"
      />
    </Drawer>
  );
}
